import React, { useState, useEffect } from 'react';
import { issuesAPI } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import { useToast } from '../../context/ToastContext';
import Pagination from '../../components/Pagination';
import { useNavigate } from 'react-router-dom';
import './Admin.css';

const Issues = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { success, error } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchIssues();
  }, []);

  const fetchIssues = async () => {
    try {
      setLoading(true);
      const response = await issuesAPI.adminGetAll();
      setIssues(Array.isArray(response.data) ? response.data : response.data?.results || []);
    } catch (err) {
      error('無法載入核心服務列表');
      console.error('Error fetching issues:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (issueId, issueTitle) => {
    if (!window.confirm(`確定要刪除「${issueTitle}」？`)) return;

    try {
      await issuesAPI.delete(issueId);
      success('已刪除');
      fetchIssues();
    } catch (err) {
      error('刪除失敗');
      console.error('Error deleting issue:', err);
    }
  };

  const handleToggleFeatured = async (issue) => {
    try {
      await issuesAPI.patch(issue.id, { is_featured: !issue.is_featured });
      success(issue.is_featured ? '已取消精選' : '已設為精選');
      fetchIssues();
    } catch (err) {
      error('更新失敗');
      console.error('Error toggling featured:', err);
    }
  };

  const handleToggleActive = async (issue) => {
    try {
      await issuesAPI.patch(issue.id, { is_active: !issue.is_active });
      success(issue.is_active ? '已停用' : '已啟用');
      fetchIssues();
    } catch (err) {
      error('更新失敗');
    }
  };

  const filteredIssues = issues.filter((issue) => {
    const matchesSearch = !search ||
      issue.title?.toLowerCase().includes(search.toLowerCase()) ||
      issue.description?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && issue.is_active) ||
      (statusFilter === 'inactive' && !issue.is_active) ||
      (statusFilter === 'featured' && issue.is_featured);
    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const totalPages = Math.ceil(filteredIssues.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedIssues = filteredIssues.slice(startIndex, startIndex + itemsPerPage);

  if (loading) return <LoadingPage message="載入核心服務中..." />;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>核心服務管理</h1>
        <div className="admin-actions">
          <input
            type="text"
            placeholder="搜尋標題、內容..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <button className="btn btn-primary" onClick={() => navigate('/admin/issues/new')}>
            新增服務
          </button>
        </div>
      </div>

      <div className="filter-tabs">
        <button className={statusFilter === 'all' ? 'active' : ''} onClick={() => setStatusFilter('all')}>
          全部 ({issues.length})
        </button>
        <button className={statusFilter === 'active' ? 'active' : ''} onClick={() => setStatusFilter('active')}>
          啟用 ({issues.filter((i) => i.is_active).length})
        </button>
        <button className={statusFilter === 'inactive' ? 'active' : ''} onClick={() => setStatusFilter('inactive')}>
          停用 ({issues.filter((i) => !i.is_active).length})
        </button>
        <button className={statusFilter === 'featured' ? 'active' : ''} onClick={() => setStatusFilter('featured')}>
          精選 ({issues.filter((i) => i.is_featured).length})
        </button>
      </div>

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>標題</th>
              <th>順序</th>
              <th>狀態</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredIssues.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '2rem' }}>
                  沒有符合條件的項目
                </td>
              </tr>
            ) : (
              paginatedIssues.map((issue) => (
                <tr key={issue.id}>
                  <td>
                    <strong>{issue.title}</strong>
                    {issue.is_featured && <span className="badge badge-star">★ 精選</span>}
                  </td>
                  <td>{issue.display_order}</td>
                  <td>
                    <span className={`status-badge ${issue.is_active ? 'status-published' : 'status-draft'}`}>
                      {issue.is_active ? '啟用' : '停用'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleToggleFeatured(issue)}
                        className={`btn btn-sm ${issue.is_featured ? 'btn-warning' : 'btn-outline'}`}
                      >
                        {issue.is_featured ? '取消精選' : '精選'}
                      </button>
                      <button
                        onClick={() => handleToggleActive(issue)}
                        className="btn btn-sm btn-outline"
                      >
                        {issue.is_active ? '停用' : '啟用'}
                      </button>
                      <button
                        onClick={() => navigate(`/services/${issue.slug}`)}
                        className="btn btn-sm btn-secondary"
                      >
                        查看
                      </button>
                      <button
                        onClick={() => navigate(`/admin/issues/${issue.id}/edit`)}
                        className="btn btn-sm btn-primary"
                      >
                        編輯
                      </button>
                      <button
                        onClick={() => handleDelete(issue.id, issue.title)}
                        className="btn btn-sm btn-danger"
                      >
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredIssues.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={setItemsPerPage}
          totalItems={filteredIssues.length}
        />
      )}
    </div>
  );
};

export default Issues;
